import { useState } from 'react';
import { api } from './api';
import { useAsync } from './hooks';
import { PageState } from './ui';
import { useUnsavedChanges } from './drafts';
import { absoluteTime, enumText } from './presentation';

export function InviteSetup({ token }: { token: string }) {
  const query = useAsync(() => api.inviteInfo(token), [token]);
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [code, setCode] = useState('');
  const [setup, setSetup] = useState<{
    challenge: string;
    otpauth: string;
    expiresIn: number;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  useUnsavedChanges(!done && Boolean(password || setup));
  return (
    <main className="invite-setup card">
      <h1>Panel hesabını kur</h1>
      <PageState loading={query.loading} error={query.err} onRetry={query.reload} />
      {done ? (
        <>
          <p role="status">
            Hesabınız hazır. Parolanız ve doğrulama kodunuzla giriş yapabilirsiniz.
          </p>
          <button
            onClick={() => {
              history.replaceState(null, '', location.pathname);
              location.reload();
            }}
          >
            Giriş ekranına git
          </button>
        </>
      ) : (
        query.data && (
          <>
            <p>
              {query.data.email} · {enumText(query.data.role)}
            </p>
            <p className="muted">Davet bitişi: {absoluteTime(query.data.expiresAt)}</p>
            {!setup ? (
              <form
                className="action-form"
                onSubmit={async (event) => {
                  event.preventDefault();
                  if (busy) return;
                  if (password !== repeat) {
                    setError('Parolalar aynı olmalı.');
                    return;
                  }
                  setBusy(true);
                  setError('');
                  try {
                    setSetup(await api.startInvite(token, password));
                    setPassword('');
                    setRepeat('');
                  } catch (ex) {
                    setError(String(ex));
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                <fieldset disabled={busy}>
                  <label>
                    Yeni panel parolası
                    <input
                      type="password"
                      required
                      minLength={12}
                      maxLength={200}
                      autoComplete="new-password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                    />
                  </label>
                  <label>
                    Parolayı tekrar gir
                    <input
                      type="password"
                      required
                      minLength={12}
                      maxLength={200}
                      autoComplete="new-password"
                      value={repeat}
                      onChange={(event) => setRepeat(event.target.value)}
                    />
                  </label>
                  <button>Doğrulama uygulamasını kur</button>
                </fieldset>
              </form>
            ) : (
              <>
                <p>
                  Doğrulama uygulamanızda aşağıdaki bağlantıyı açın veya anahtarı elle ekleyin. Bu
                  anahtarı kimseyle paylaşmayın. Kurulum 10 dakika içinde tamamlanmalıdır.
                </p>
                <a href={setup.otpauth}>Doğrulama uygulamasında aç</a>
                <label>
                  Elle kurulum anahtarı
                  <input
                    readOnly
                    value={new URL(setup.otpauth).searchParams.get('secret') ?? ''}
                    onFocus={(event) => event.target.select()}
                  />
                </label>
                <form
                  className="action-form"
                  onSubmit={async (event) => {
                    event.preventDefault();
                    if (busy) return;
                    setBusy(true);
                    setError('');
                    try {
                      await api.finishInvite(token, setup.challenge, code);
                      setSetup(null);
                      setCode('');
                      setDone(true);
                    } catch (ex) {
                      setError(String(ex));
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  <label>
                    6 haneli doğrulama kodu
                    <input
                      required
                      pattern="[0-9]{6}"
                      maxLength={6}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={code}
                      onChange={(event) => setCode(event.target.value)}
                      disabled={busy}
                    />
                  </label>
                  <button disabled={busy}>Hesabı etkinleştir</button>
                </form>
                <button
                  disabled={busy}
                  onClick={() => {
                    setSetup(null);
                    setCode('');
                    setError('');
                  }}
                >
                  Kurulumu yeniden başlat
                </button>
              </>
            )}
          </>
        )
      )}
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
    </main>
  );
}
